import { FC, JSX, useMemo } from "react";
import { EllipsisTooltip } from "@app/shared/EllipsisTooltip";
import { COUNTY_SUMMARY_POPUP_ENROLLMENT_COLOR } from "@app/overviews/market-expansion-dashboard/MarketExpansionDashboardConstants";
import { MedicarePayerEnrollment } from "@app/overviews/market-expansion-dashboard/shared/MedicarePayerEnrollment";

const TOP_PAYERS_LIMIT = 5;

interface MedicareTopPayersTableProps {
  payers: MedicarePayerEnrollment[];
}

const MedicareTopPayersTable: FC<MedicareTopPayersTableProps> = ({payers}: MedicareTopPayersTableProps): JSX.Element => {
  const sortedPayers = useMemo(
    () => payers.slice().sort((a, b) => b.enrollment - a.enrollment).slice(0, TOP_PAYERS_LIMIT),
    [payers]
  );

  const formatYearOverYearChange = (value: number | null | undefined): string => {
    if (value == null) {
      return "---"
    };

    const rounded = (value * 100).toFixed(0);
    return `${rounded === "-0" ? "0" : rounded}%`;
  };

  const maxPayerEnrollment = sortedPayers.length > 0 ? sortedPayers[0].enrollment : 0;

  if (!sortedPayers.length) {
    return <></>;
  }

  return (
    <table className="county-summary-popup__table">
      <thead>
        <tr>
          <th className="payer-title column-title" scope="col">Payer</th>
          <th className="enrollment-title column-title" scope="col">Enrollment</th>
          <th className="percent-change-title column-title" scope="col">% Change (YoY)</th>
        </tr>
        <tr>
          <td colSpan={3}>
            <div className="county-summary-popup__separator" />
          </td>
        </tr>
      </thead>
      <tbody>
        {sortedPayers.map((payer: MedicarePayerEnrollment, index: number) => {
          const proportion = maxPayerEnrollment > 0
            ? (payer.enrollment / maxPayerEnrollment) * 100
            : 0;

          return (
            <tr key={payer.payerName} data-testid={`medicare-top-payers-table__row-${index + 1}`}>
              <td className="payer-data" data-testid={`medicare-top-payers-table__payer-name-${payer.payerName}`}>
                <EllipsisTooltip text={payer.payerName} width="100%" position="top" />
              </td>
              <td className="enrollment-data" data-testid={`medicare-top-payers-table__enrollment-count-${payer.payerName}`}>
                <div
                  className="enrollment-data-background"
                  style={{
                    backgroundColor: `${COUNTY_SUMMARY_POPUP_ENROLLMENT_COLOR}`,
                    width: `${proportion}%`,
                  }}
                />
                <span className="enrollment-data-text">
                  {payer.enrollment.toLocaleString()}
                </span>
              </td>
              <td className="percent-change-data" data-testid={`medicare-top-payers-table__enrollment-change-${payer.payerName}`}>
                {formatYearOverYearChange(payer.enrollmentYearOverYearChange)}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

export default MedicareTopPayersTable;
